import { Link as RouterLink } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  size?: 'default' | 'small';
}

export default function ComicCard({ comic, size = 'default' }: ComicCardProps) {
  const isSmall = size === 'small';
  const ratingStars = Array.from({ length: 5 }, (_, i) => {
    const starVal = comic.rating / 2;
    if (i < Math.floor(starVal)) return 'ri-star-fill text-amber-400';
    if (i < starVal) return 'ri-star-half-fill text-amber-400';
    return 'ri-star-line text-foreground-400';
  });

  const categoryMap: Record<string, string> = {
    romance: '恋爱',
    action: '动作',
    fantasy: '奇幻',
    drama: '剧情',
    thriller: '悬疑',
    comedy: '搞笑',
    bl: 'BL',
    completed: '已完结',
  };

  return (
    <RouterLink
      to={`/article/${comic.id}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
      data-product-shop
    >
      <div className="relative overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 无删减韩国漫画在线阅读`}
          className={`w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${isSmall ? 'h-52' : 'h-64'}`}
          width={isSmall ? 280 : 400}
          height={isSmall ? 364 : 560}
        />
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-foreground-500 text-background-50'}`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-700 whitespace-nowrap">
            {comic.chapters}话
          </span>
        </div>
      </div>
      <div className={`${isSmall ? 'p-3' : 'p-4'}`}>
        <h3 className={`font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 ${isSmall ? 'text-sm' : 'text-base'}`}>
          {comic.title}
        </h3>
        <p className={`text-foreground-600 mt-1 ${isSmall ? 'text-xs' : 'text-sm'}`}>
          {comic.author}
        </p>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-0.5">
            {ratingStars.map((cls, i) => (
              <i key={i} className={`${cls} ${isSmall ? 'text-xs' : 'text-sm'}`}></i>
            ))}
            <span className={`ml-1 font-semibold text-foreground-950 ${isSmall ? 'text-xs' : 'text-sm'}`}>{comic.rating}</span>
          </div>
          <span className={`text-foreground-500 ${isSmall ? 'text-xs' : 'text-sm'}`}>
            {(comic.views / 10000).toFixed(0)}万阅读
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {comic.tags.slice(0, 2).map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </RouterLink>
  );
}